import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { FilaEsperaService } from '../Service';
import { useAuthContext } from '../../../contexts/AuthContext';
import { WebSocketManager } from '../../../WebSocketConfig';

const service = new FilaEsperaService();

interface WaitlistEntry {
    _id: string;
    status: string;
    client?: { uid: string; name: string; phoneNumber?: string } | null;
    attendant?: { uid: string; name: string } | null;
    servico?: { _id: string; name: string } | null;
    servicosExtras?: { _id: string; name: string }[] | null;
    servicosExtrasIds?: string[];
    preferredStartAt?: string | Date;
    preferredEndAt?: string | Date;
    timeWindowStartMinutes?: number;
    timeWindowEndMinutes?: number;
    totalDuration?: number;
    createdAt?: string | Date;
}

interface FilaEsperaContextProps {
    data: WaitlistEntry[];
    loading: boolean;
    error: boolean;
    reloadData: () => Promise<void>;
}

const FilaEsperaContext = createContext<FilaEsperaContextProps | undefined>(undefined);

export const useFilaEsperaContext = () => {
    const context = useContext(FilaEsperaContext);
    if (!context) {
        throw new Error('useFilaEsperaContext must be used within a FilaEsperaProvider');
    }
    return context;
};

export const FilaEsperaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState<WaitlistEntry[]>([]);

    const reloadData = useCallback(async () => {
        try {
            setError(false);
            const result = await service.findAll().then((response) => response?.data || []);
            setData(result);
        } catch (error) {
            console.error('Error fetching fila de espera:', error);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reloadData();
    }, [reloadData]);

    useEffect(() => {
        if (!user?.estabIdSession) return;
        const webSocketManager = new WebSocketManager();
        webSocketManager.onChannel(`fila-espera:estab:${user.estabIdSession}`, () => {
            reloadData();
        });

        return () => {
            webSocketManager.disconnect();
        };
    }, [user?.estabIdSession, reloadData]);

    return (
        <FilaEsperaContext.Provider value={{ data, loading, error, reloadData }}>
            {children}
        </FilaEsperaContext.Provider>
    );
};
